
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Upload, Search, ArrowRight, Globe, Sparkles } from 'lucide-react';
import { UserPreferences } from '@/api/entities';

const allSelectionOptions = [
{
  title: 'Search Flights',
  description: 'Search flights and get real-time prices and availability.',
  icon: Globe,
  url: '/search',
  engineKey: 'googleFlights'
}];


export default function BookPage() {
  const [selectionOptions, setSelectionOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load search engine preferences from Supabase
    const loadPreferences = async () => {
      try {
        const userPrefs = await UserPreferences.get();
        const preferences = userPrefs.searchEnginePreferences;
        
        // Filter options based on enabled search engines
        const filteredOptions = allSelectionOptions.filter(option => {
          // Always show Upload Itinerary (it's not a search engine)
          if (option.engineKey === null) return true;
          // Only show if the engine is enabled
          return preferences[option.engineKey] !== false;
        });
        
        setSelectionOptions(filteredOptions);
      } catch (error) {
        console.error('Failed to load preferences:', error);
        // On error, show all options
        setSelectionOptions(allSelectionOptions);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadPreferences();
    
    // Reload preferences when page becomes visible (user navigates back)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadPreferences();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', loadPreferences);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', loadPreferences);
    };
  }, []);

  return (
    <div className="min-h-[calc(70vh-56px)] flex items-center justify-center relative overflow-hidden bg-black">
      <div className="max-w-6xl mx-auto px-6 md:px-8 py-16 relative z-10">
        {/* Hero Section */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 mb-6">
            <Sparkles className="w-5 h-5 text-white/40" />
            <span className="text-white/40 text-sm font-normal tracking-widest uppercase">Begin Your Adventure</span>
          </div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-light text-white mb-4 tracking-tight">
            Start Your Journey
          </h1>
          <p className="text-white/60 text-sm md:text-base lg:text-lg font-light max-w-md mx-auto">
            Choose how you'd like to plan your next adventure
          </p>
        </motion.div>

        {/* Options Grid */}
        {!isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto items-stretch">
            {selectionOptions.map((option, index) => (
              <Link to={option.url} key={option.title + index} className="flex">
                <motion.div
                  className="group relative overflow-hidden flex-1"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                >
                  {/* Card Background */}
                  <div className="absolute inset-0 bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 transition-all duration-500 group-hover:bg-white/10 group-hover:border-white/20" />
                  
                  {/* Subtle Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  {/* Content */}
                  <div className="relative p-10">
                    {/* Icon */}
                    <div className="mb-8">
                      <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center transition-all duration-500 group-hover:bg-white/20 group-hover:scale-110">
                        <option.icon className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    
                    {/* Text Content */}
                    <div className="mb-8">
                      <h3 className="text-2xl font-light text-white mb-3 tracking-tight">
                        {option.title}
                      </h3>
                      <p className="text-white/60 text-sm font-normal leading-relaxed">
                        {option.description}
                      </p>
                    </div>
                    
                    {/* Action */}
                    <div className="flex items-center gap-2 text-white/80 group-hover:text-white transition-colors duration-300">
                      <span className="text-sm font-normal tracking-wide">Continue</span>
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-2" />
                    </div>
                  </div>
                  
                  {/* Hover Glow Effect */}
                  <div className="absolute -inset-[1px] bg-gradient-to-r from-white/20 via-white/10 to-white/20 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl -z-10" />
                </motion.div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Slow elegant background tunnel animation styles */}
      <style>{`
        /* Perspective tunnel container - background */
        .perspective-container-bg {
          perspective: 800px;
          perspective-origin: 50% 50%;
          opacity: 0.15;
        }
        
        /* Flying tunnel hexagons - SLOW and ELEGANT */
        .tunnel-hexagons-bg {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .giant-hexagon-bg {
          position: absolute;
          width: 350px;
          height: 350px;
          background: linear-gradient(
            60deg,
            transparent 30%,
            rgba(71, 117, 245, 0.2) 45%,
            rgba(71, 117, 245, 0.35) 50%,
            rgba(71, 117, 245, 0.2) 55%,
            transparent 70%
          );
          clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
          animation: tunnel-fly-bg 5s ease-in-out infinite;
          box-shadow: 
            0 0 60px rgba(71, 117, 245, 0.3),
            inset 0 0 40px rgba(71, 117, 245, 0.15);
        }
        
        @keyframes tunnel-fly-bg {
          0% { 
            transform: translateZ(-1000px) scale(0.3) rotate(0deg); 
            opacity: 0;
          }
          15% {
            opacity: 0.6;
          }
          50% { 
            transform: translateZ(0px) scale(1.5) rotate(180deg); 
            opacity: 0.8;
            box-shadow: 
              0 0 80px rgba(71, 117, 245, 0.4),
              inset 0 0 50px rgba(71, 117, 245, 0.2);
          }
          85% {
            opacity: 0.6;
          }
          100% { 
            transform: translateZ(800px) scale(3) rotate(360deg); 
            opacity: 0;
          }
        }
        
        /* Giant pulsing circles - SLOW and ELEGANT */
        .immersive-circles-bg {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .mega-circle-bg {
          position: absolute;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          border: 3px solid rgba(71, 117, 245, 0.25);
          filter: drop-shadow(0 0 40px rgba(71, 117, 245, 0.4));
          animation: mega-expand-bg 5.5s ease-out infinite;
        }
        
        @keyframes mega-expand-bg {
          0% { 
            transform: scale(0.3);
            opacity: 0;
            border-width: 2px;
            filter: drop-shadow(0 0 20px rgba(71, 117, 245, 0.25));
          }
          15% {
            opacity: 0.6;
          }
          50% { 
            transform: scale(2.5);
            opacity: 0.7;
            border-width: 5px;
            filter: drop-shadow(0 0 60px rgba(71, 117, 245, 0.6));
          }
          100% { 
            transform: scale(4);
            opacity: 0;
            border-width: 1px;
            filter: drop-shadow(0 0 30px rgba(71, 117, 245, 0.2));
          }
        }
        
        /* Glittering star field - VERY SUBTLE */
        .glitter-field-bg {
          position: absolute;
          inset: 0;
        }
        .glitter-star-bg {
          position: absolute;
          width: 4px;
          height: 4px;
          background: rgba(71, 117, 245, 0.8);
          border-radius: 50%;
          animation: glitter-twinkle-bg 3s ease-in-out infinite;
          box-shadow: 
            0 0 15px rgba(71, 117, 245, 0.6),
            0 0 30px rgba(71, 117, 245, 0.4);
        }
        
        @keyframes glitter-twinkle-bg {
          0%, 100% { 
            opacity: 0;
            transform: scale(0.5) rotate(0deg);
            box-shadow: 0 0 8px rgba(71, 117, 245, 0.3);
          }
          50% { 
            opacity: 0.8;
            transform: scale(1.3) rotate(180deg);
            box-shadow: 
              0 0 20px rgba(71, 117, 245, 0.7),
              0 0 40px rgba(71, 117, 245, 0.5);
          }
        }
      `}</style>
    </div>
  );
}
